import Vue from 'vue';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation. All rights reserved.
Licensed under the Apache License, Version 2.0 (the "License"); you may not use
this file except in compliance with the License. You may obtain a copy of the
License at http://www.apache.org/licenses/LICENSE-2.0

THIS CODE IS PROVIDED ON AN *AS IS* BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
KIND, EITHER EXPRESS OR IMPLIED, INCLUDING WITHOUT LIMITATION ANY IMPLIED
WARRANTIES OR CONDITIONS OF TITLE, FITNESS FOR A PARTICULAR PURPOSE,
MERCHANTABLITY OR NON-INFRINGEMENT.

See the Apache Version 2.0 License for specific language governing permissions
and limitations under the License.
***************************************************************************** */

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

function __generator(thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
}

var isUnknownObject = function (raw) {
  return raw !== null && typeof raw === "object";
};
// but in an untyped integration `stripe` could be anything, so we need
// to do some sanity validation to prevent type errors.

var isStripe = function (raw) {
  return isUnknownObject(raw) && typeof raw.elements === "function" && typeof raw.createToken === "function" && typeof raw.createPaymentMethod === "function" && typeof raw.confirmCardPayment === "function";
};
var INVALID_STRIPE_ERROR = "Invalid prop `stripe` supplied to `Elements`. We recommend using the `loadStripe` utility from `@stripe/stripe-js`. See https://stripe.com/docs/stripe-js/react#elements-props-stripe for details."; // We are using types to enforce the `stripe` prop in this lib, but in a real
// integration `stripe` could be anything, so we need to do some sanity
// validation to prevent type errors.

var validateStripe = function (maybeStripeP) {
  return __awaiter(void 0, void 0, void 0, function () {
    var maybeStripe;
    return __generator(this, function (_a) {
      switch (_a.label) {
        case 0:
          return [4
          /*yield*/
          , maybeStripeP];

        case 1:
          maybeStripe = _a.sent();

          if (maybeStripe === null || isStripe(maybeStripe)) {
            return [2
            /*return*/
            , maybeStripe];
          }

          throw new Error(INVALID_STRIPE_ERROR);
      }
    });
  });
};

var PLAIN_OBJECT_STR = "[object Object]";
var isEqual = function (left, right) {
  if (!isUnknownObject(left) || !isUnknownObject(right)) {
    return left === right;
  }

  var leftArray = Array.isArray(left);
  var rightArray = Array.isArray(right);
  if (leftArray !== rightArray) return false;
  var leftPlainObject = Object.prototype.toString.call(left) === PLAIN_OBJECT_STR;
  var rightPlainObject = Object.prototype.toString.call(right) === PLAIN_OBJECT_STR;
  if (leftPlainObject !== rightPlainObject) return false;
  if (!leftPlainObject && !leftArray) return false;
  var leftKeys = Object.keys(left);
  var rightKeys = Object.keys(right);
  if (leftKeys.length !== rightKeys.length) return false;
  var keySet = {};

  for (var i = 0; i < leftKeys.length; i += 1) {
    keySet[leftKeys[i]] = true;
  }

  for (var i = 0; i < rightKeys.length; i += 1) {
    keySet[rightKeys[i]] = true;
  }

  var allKeys = Object.keys(keySet);

  if (allKeys.length !== leftKeys.length) {
    return false;
  }

  var l = left;
  var r = right;

  var pred = function (key) {
    return isEqual(l[key], r[key]);
  };

  return allKeys.every(pred);
};

var i$stripe = "i$stripe";
function makeDollarStripe(stripe, elementOptions) {
  var promise = validateStripe(stripe).then(function (stripe) {
    return stripe && {
      stripe: stripe,
      elements: stripe.elements(elementOptions)
    };
  });
  var $stripe = Vue.observable({
    stripe: null,
    elements: null,
    promise: promise
  });
  promise.then(function (vueStripe) {
    if (vueStripe) {
      $stripe.stripe = vueStripe.stripe;
      $stripe.elements = vueStripe.elements;
    }
  });
  return $stripe;
}

var capitalized = function (str) {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

var extractUpdateableOptions = function (options) {
  if (!isUnknownObject(options)) {
    return {};
  } // eslint-disable-next-line @typescript-eslint/no-unused-vars


  var _ = options.paymentRequest,
      rest = __rest(options, ["paymentRequest"]);

  return rest;
};

function createElementComponent(type, isServer) {
  var name = capitalized(type) + "Element"; // Type needs to be explicit or compiler gets confused

  var props = {
    options: {
      required: false,
      type: Object,
      default: function () {
        return {};
      }
    }
  };
  var Element = isServer ? Vue.extend({
    name: name,
    props: props,
    render: function (h) {
      return h("div", {
        attrs: this.$attrs
      });
    }
  }) : Vue.extend({
    name: name,
    props: props,
    data: function () {
      return {
        element: null
      };
    },
    inject: {
      i$stripe: {
        from: i$stripe,
        default: null
      }
    },
    beforeMount: function () {
      var _this = this;

      if (this.element) {
        return;
      }

      var dStripe = this.i$stripe || this.$stripe;

      if (!dStripe) {
        throw new Error("Need to install VueStripe plugin or provide with Elements component");
      }

      if (!dStripe.elements) {
        throw new Error("Stripe was not yet loaded or error");
      }

      var element = dStripe.elements.create(type, this.options);
      this.element = element;
      element.on("ready", function () {
        return _this.$emit("ready", _this.element);
      });
      element.on("change", function (event) {
        return _this.$emit("change", event);
      });
      element.on("blur", function () {
        return _this.$emit("blur");
      });
      element.on("focus", function () {
        return _this.$emit("focus");
      });
      element.on("escape", function () {
        return _this.$emit("escape");
      }); // Users can pass an an onClick prop on any Element component
      // just as they could listen for the `click` event on any Element,
      // but only the PaymentRequestButton will actually trigger the event.

      element.on("click", function () {
        return _this.$emit("click");
      });
    },
    mounted: function () {
      if (!this.element) {
        return;
      }

      this.element.mount(this.$el);
    },
    beforeDestroy: function () {
      if (this.element) {
        this.element.unmount();
        this.element.destroy();
      }
    },
    watch: {
      options: function (options, prevOptions) {
        if (prevOptions && prevOptions.paymentRequest !== options.paymentRequest) {
          console.warn("Unsupported prop change: options.paymentRequest is not a customizable property.");
        }

        var updateableOptions = extractUpdateableOptions(options);

        if (Object.keys(updateableOptions).length !== 0 && !isEqual(updateableOptions, extractUpdateableOptions(prevOptions))) {
          if (this.element) {
            this.element.update(updateableOptions);
          }
        }
      }
    },
    methods: {
      blur: function () {
        var _a;

        (_a = this.element) === null || _a === void 0 ? void 0 : _a.blur();
      },
      clear: function () {
        var _a;

        (_a = this.element) === null || _a === void 0 ? void 0 : _a.clear();
      },
      focus: function () {
        var _a;

        (_a = this.element) === null || _a === void 0 ? void 0 : _a.focus();
      }
    },
    render: function (h) {
      return h("div", {
        attrs: this.$attrs
      });
    }
  });
  Element.__elementType = type;
  return Element;
}

var _a;
var Elements = Vue.extend({
  name: 'Elements',
  props: {
    stripe: {
      required: true,
      type: [String, Object, Promise]
    },
    options: {
      required: false,
      type: Object
    }
  },
  provide: function () {
    var _a;

    return _a = {}, _a[i$stripe] = makeDollarStripe(this.stripe, this.options), _a;
  },
  render: function (h) {
    var _a, _b; // TODO use vue fragments


    var defaultSlotChildren = (_b = (_a = this.$scopedSlots) === null || _a === void 0 ? void 0 : _a.default) === null || _b === void 0 ? void 0 : _b.call(_a, {});

    if (defaultSlotChildren && defaultSlotChildren.length === 1) {
      return defaultSlotChildren[0];
    } else {
      return h('div', defaultSlotChildren);
    }
  }
});
var ElementsConsumer = Vue.extend({
  name: 'ElementsConsumer',
  inject: (_a = {}, _a[i$stripe] = {
    default: null
  }, _a),
  render: function (h) {
    var _a, _b, _c, _d; // TODO use vue fragments


    var defaultSlotChildren = (_b = (_a = this.$scopedSlots) === null || _a === void 0 ? void 0 : _a.default) === null || _b === void 0 ? void 0 : _b.call(_a, {
      stripe: (_c = this.i$stripe) === null || _c === void 0 ? void 0 : _c.stripe,
      elements: (_d = this.i$stripe) === null || _d === void 0 ? void 0 : _d.elements
    });

    if (defaultSlotChildren && defaultSlotChildren.length === 1) {
      return defaultSlotChildren[0];
    } else {
      return h('div', defaultSlotChildren);
    }
  }
});

var isServer = typeof window === "undefined";
/**
 * Requires beta access:
 * Contact [Stripe support](https://support.stripe.com/) for more information.
 *
 * @docs https://stripe.com/docs/stripe-js/react#element-components
 */

var AuBankAccountElement = createElementComponent("auBankAccount", isServer);
/**
 * @docs https://stripe.com/docs/stripe-js/react#element-components
 */

var CardElement = createElementComponent("card", isServer);
/**
 * @docs https://stripe.com/docs/stripe-js/react#element-components
 */

var CardNumberElement = createElementComponent("cardNumber", isServer);
/**
 * @docs https://stripe.com/docs/stripe-js/react#element-components
 */

var CardExpiryElement = createElementComponent("cardExpiry", isServer);
/**
 * @docs https://stripe.com/docs/stripe-js/react#element-components
 */

var CardCvcElement = createElementComponent("cardCvc", isServer);
/**
 * @docs https://stripe.com/docs/stripe-js/react#element-components
 */

var FpxBankElement = createElementComponent("fpxBank", isServer);
/**
 * @docs https://stripe.com/docs/stripe-js/react#element-components
 */

var IbanElement = createElementComponent("iban", isServer);
/**
 * @docs https://stripe.com/docs/stripe-js/react#element-components
 */

var IdealBankElement = createElementComponent("idealBank", isServer);
/**
 * @docs https://stripe.com/docs/stripe-js/react#element-components
 */

var PaymentRequestButtonElement = createElementComponent("paymentRequestButton", isServer);

var components = /*#__PURE__*/Object.freeze({
    __proto__: null,
    AuBankAccountElement: AuBankAccountElement,
    CardElement: CardElement,
    CardNumberElement: CardNumberElement,
    CardExpiryElement: CardExpiryElement,
    CardCvcElement: CardCvcElement,
    FpxBankElement: FpxBankElement,
    IbanElement: IbanElement,
    IdealBankElement: IdealBankElement,
    PaymentRequestButtonElement: PaymentRequestButtonElement,
    Elements: Elements,
    ElementsConsumer: ElementsConsumer
});

var install = function installVueStripeJs(Vue$1, options) {
  return __awaiter(this, void 0, void 0, function () {
    return __generator(this, function (_a) {
      if (install.installed) return [2
      /*return*/
      ];
      install.installed = true;

      if (Vue !== Vue$1) {
        console.error('Multiple instances of Vue detected\nSee https://github.com/vuetifyjs/vuetify/issues/4068\n\nIf you\'re seeing "$attrs is readonly", it\'s caused by this');
      }

      Object.entries(components).forEach(function (_a) {
        var componentName = _a[0],
            component = _a[1];
        Vue$1.component(componentName, component);
      });

      if (!options || typeof options.stripe === 'undefined') {
        Vue$1.prototype.$stripe = new Proxy({}, {
          get: function () {
            throw new Error("Since no Stripe instance has been provided please use the Elements component to provide it.");
          }
        });
      } else {
        Vue$1.prototype.$stripe = makeDollarStripe(options.stripe, options.elementOptions);
      }

      return [2
      /*return*/
      ];
    });
  });
};

export default install;
export { AuBankAccountElement, CardCvcElement, CardElement, CardExpiryElement, CardNumberElement, Elements, ElementsConsumer, FpxBankElement, IbanElement, IdealBankElement, PaymentRequestButtonElement };
